import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  useDisclosure,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  VStack,
} from '@chakra-ui/react';
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { CreditFacilityGQLType } from '../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { colors } from '~/packages/common-frontend/colors';
import { SearchBarInput } from '~/packages/common-frontend/components/SearchAndCopyComponents';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { tableHeaderStyle } from '~/packages/common-frontend/styles';
import { useGetMultipleExternalFacilityIdsQuery } from '~/packages/credit-facility/requests/requests.gql.generated';

type CreditFacility = Pick<
  CreditFacilityGQLType,
  'id' | 'name' | 'externalFacilityId'
>;

type FacilityOption = {
  value: string;
  facility: CreditFacility;
};

const TableCell = (cellValue: string) => {
  return (
    <Td overflow="hidden" whiteSpace="normal">
      {cellValue}
    </Td>
  );
};

const TableRow = ({
  facilityOption,
  onSelect,
}: {
  facilityOption: FacilityOption;
  onSelect: (option: FacilityOption) => void;
}) => {
  const facilityValue = `${facilityOption.facility.name}${
    facilityOption.facility?.externalFacilityId
      ? ` (${facilityOption.facility.externalFacilityId})`
      : ''
  }`;
  return (
    <Tr
      _hover={{ backgroundColor: colors.gray[50] }}
      cursor="pointer"
      onClick={() => onSelect(facilityOption)}
    >
      {TableCell(facilityValue)}
    </Tr>
  );
};

const TableHeaderCell = (label: string) => {
  return (
    <Th
      sx={{
        ...tableHeaderStyle,
        padding: '',
        position: 'sticky',
        top: 0,
      }}
    >
      {label}
    </Th>
  );
};

const OriginateLoansTable = ({
  options,
  onSelectFacilityOption,
}: {
  options: FacilityOption[];
  onSelectFacilityOption: (option: FacilityOption) => void;
}) => {
  return (
    <TableContainer maxH="22rem" minW="100%" overflowY="scroll">
      <Table>
        <Thead>
          <Tr>{TableHeaderCell('Facility')}</Tr>
        </Thead>
        <Tbody>
          {options.map((option) => (
            <TableRow
              key={option.value}
              facilityOption={option}
              onSelect={onSelectFacilityOption}
            />
          ))}
          {options.length === 0 && (
            <Tr>
              <Td colSpan={3} textAlign="center">
                No results found.
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export const OriginateLoanModal = ({
  modalControls,
}: {
  modalControls: ReturnType<typeof useDisclosure>;
}) => {
  const { facilities } = useCreditFacilitiesOrThrow();

  const getExternalFacilityIdsQuery = useGetMultipleExternalFacilityIdsQuery({
    variables: {
      creditFacilityIds: facilities.map((facility) => facility.id),
    },
  });
  if (getExternalFacilityIdsQuery.error) {
    throw getExternalFacilityIdsQuery.error;
  }

  const [creditFacilityId, setCreditFacilityId] = useState<string | undefined>(
    () => facilities[0]?.id,
  );
  const [searchQuery, setSearchQuery] = useState('');

  const onClose = () => {
    setSearchQuery('');
    modalControls.onClose();
  };

  const onSelectFacilityOption = (option: FacilityOption) => {
    setCreditFacilityId(option.value);
    setSearchQuery(option.facility.name);
  };

  const options = useMemo(() => {
    if (!facilities || !getExternalFacilityIdsQuery.data) return [];

    const externalFacilityIds = getExternalFacilityIdsQuery.data.result;
    return facilities.map((facility) => {
      const externalFacilityId =
        externalFacilityIds.find((x) => x.creditFacilityId === facility.id)
          ?.externalFacilityId ?? null;

      return {
        label: facility.id,
        value: facility.id,
        facility: { ...facility, externalFacilityId },
      };
    });
  }, [facilities, getExternalFacilityIdsQuery.data]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) {
      return options;
    }

    const search = searchQuery.toLowerCase();
    const isMatch = (input?: string | null): boolean => {
      if (!input) return false;
      return input.toLowerCase().includes(search);
    };

    return options.filter((option) => {
      return (
        isMatch(option.facility.name) ||
        isMatch(option.facility.externalFacilityId)
      );
    });
  }, [options, searchQuery]);

  return (
    <Modal {...modalControls} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb="0.5rem">
          <Typography variant="section-subtitle">Originate Loan</Typography>
          <Typography fontWeight="medium">Select Facility</Typography>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody mb="0.5rem" gap="1rem" as={VStack} alignItems="flex-start">
          <SearchBarInput query={searchQuery} onChangeQuery={setSearchQuery} />
          <OriginateLoansTable
            options={filteredOptions}
            onSelectFacilityOption={onSelectFacilityOption}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={!creditFacilityId}
            colorScheme="v2-aurora"
            variant="primary"
            as={Link}
            to={{
              pathname: '/funding-requests/create',
              search: `?creditFacilityId=${creditFacilityId}`,
            }}
          >
            Next
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
